// To add a new provider:
// 1. If the brand has predictable regional TLD variants (e.g. brand.com, brand.co.uk, brand.com.au),
//    add a prefix regex to PREFIX_PATTERNS below.
// 2. If the brand has a fixed set of domains, add them to EXACT_DOMAIN_MAP.
// 3. Add the new key to ProviderCategory.
// 4. Add an entry to PROVIDER_HINT_CONFIG in emailProviderConfig.ts.

export type ProviderCategory =
  | 'microsoft_consumer'
  | 'yahoo_family'
  | 'gmail'
  | 'zoho'
  | 'apple'
  | 'other';

// Exact-match for providers whose domains don't vary by country TLD.
const EXACT_DOMAIN_MAP: Record<string, ProviderCategory> = {
  // apple (3 fixed global domains)
  'icloud.com': 'apple',
  'mac.com': 'apple',
  'me.com': 'apple',
  // gmail (2 fixed global domains)
  'gmail.com': 'gmail',
  'googlemail.com': 'gmail',
  // microsoft_consumer (msn has no regional variants)
  'msn.com': 'microsoft_consumer',
  // yahoo_family ISP domains (US/UK carriers — no regional TLD variants)
  'aol.co.uk': 'yahoo_family',
  'aol.com': 'yahoo_family',
  'att.net': 'yahoo_family',
  'btinternet.com': 'yahoo_family',
  'sbcglobal.net': 'yahoo_family',
  'verizon.net': 'yahoo_family',
  // zoho (2 fixed global domains)
  'zoho.com': 'zoho',
  'zohomail.com': 'zoho',
};

// Prefix regex for providers whose brand name is the domain prefix across all country TLDs.
// Matches outlook.com, outlook.com.au, outlook.co.jp, hotmail.es, live.fr, yahoo.com.br, etc.
const PREFIX_PATTERNS: Array<[RegExp, ProviderCategory]> = [
  [/^(outlook|hotmail|live)\./, 'microsoft_consumer'],
  [/^(yahoo|ymail|rocketmail|myyahoo)\./, 'yahoo_family'],
];

export function detectEmailProvider(email: string): ProviderCategory {
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex === -1 || atIndex === trimmed.length - 1) return 'other';
  const domain = trimmed.slice(atIndex + 1);

  const exact = EXACT_DOMAIN_MAP[domain];
  if (exact) return exact;

  for (const [pattern, category] of PREFIX_PATTERNS) {
    if (pattern.test(domain)) return category;
  }

  return 'other';
}
